import { useAuth0 } from "@auth0/auth0-react"
import { UiSurface, UiSurfaceId } from "shared/ui/uiSurface"
import * as Sentry from "@sentry/react"
import { FC, Suspense, useEffect } from "react"
import {
  BrowserRouter,
  Navigate,
  Route,
  RouteObject,
  Routes,
} from "react-router"
import { Dashboard, ProtectedRoute, PublicHomePage } from "./app/components"
import { HomePage } from "./app/components/HomePage"
import { useMe } from "./app/hooks"
import { ConfigSetting } from "./features/config-setting/components"
import { RoutePlanningContainer } from "./features/route-planning/components"
import { pathConstants } from "./features/shared-module/constants"

const SentryRoutes =
  import.meta.env.VITE_DISABLE_SENTRY === "true"
    ? Routes
    : Sentry.withSentryReactRouterV7Routing(Routes)

export type MobilisRouteObject = RouteObject & {
  isPublic?: boolean
  ui?: UiSurfaceId
}

export const allRoutes: MobilisRouteObject[] = [
  {
    path: `${pathConstants.login}`,
    isPublic: true,
    ui: "UI-DSP-001",
    element: <PublicHomePage />,
  },
  {
    path: `${pathConstants.defaultRoute}`,
    element: <HomePage />,
    ui: "UI-DSP-003",
    isPublic: false,
    children: [
      {
        path: `${pathConstants.empty}`,
        element: <Navigate to={`/${pathConstants.dashboard}`} replace />,
      },
      {
        path: `/${pathConstants.settings}`,
        ui: "UI-DSP-050",
        element: <ConfigSetting />,
      },
      {
        path: `/${pathConstants.dashboard}`,
        ui: "UI-DSP-010",
        element: <Dashboard />,
      },
      {
        path: `/${pathConstants.routePlanning}`,
        ui: "UI-DSP-040",
        element: <RoutePlanningContainer />,
      },
    ] as MobilisRouteObject[],
  },
  {
    path: pathConstants.wildCard,
    isPublic: true,
    ui: "UI-DSP-007",
    element: <>ERROR...</> /* TODO: add component later */,
  },
]

export const AppRoutes: FC = () => {
  const { isLoading, isAuthenticated } = useAuth0()
  const { fetchMe, isLoading: idpLoading } = useMe()

  useEffect(() => {
    if (isAuthenticated) {
      fetchMe()
    }
  }, [fetchMe, isAuthenticated])

  return (
    <BrowserRouter>
      <Suspense fallback={<>Loading...</>}>
        <SentryRoutes>
          {renderRoutes(allRoutes, { isLoading, isAuthenticated, idpLoading })}
        </SentryRoutes>
      </Suspense>
    </BrowserRouter>
  )
}

const renderRoutes = (routes: MobilisRouteObject[], props: any) => {
  return routes.map((route, index) => {
    const routeElement = route.ui ? (
      <UiSurface id={route.ui}>{route.element}</UiSurface>
    ) : (
      route.element
    )
    const element = route?.isPublic ? (
      routeElement
    ) : (
      <ProtectedRoute {...props}>{routeElement}</ProtectedRoute>
    )
    return (
      <Route key={index} path={route.path} element={element}>
        {route.children && renderRoutes(route.children, props)}
      </Route>
    )
  })
}
